/**
 * AI:
 * Status: "in progress"
 * Type: Service
 * SubType: WidgetConfig
 * Reason: Curated column layout for the review tables of the run detail — the same shape as
 *         `buildCpWidgetForm`, plus an optional output control per column so a raw LocalDateTime
 *         renders as a German date/time and an id renders as plain text.
 */
import {WidgetForm} from 'features/core/shared/widget/widget-form';
import {WidgetConfigFormLayout} from 'features/core/shared/widget/widget-config-form-layout';
import {WidgetConfigFormField} from 'features/core/shared/widget/widget-config-form-field';
import {CpWidgetColumn} from './cp-widget-column';

/**
 * One curated column of a run-detail review table.
 *
 * `outputControl` is the NAME of a registered output control (`MvsFormFieldService` output map,
 * e.g. `dateTime`, `text`, `textShowMore`). It is applied through `WidgetConfigFormField.uiOutputField`,
 * the documented per-column override of `WidgetService` — no custom template, no saved layout needed.
 */
export interface CpRunWidgetColumn extends CpWidgetColumn {
    outputControl?: string;
}

/**
 * Build the default layout of a curated run-detail table (`WidgetDataParam.create('widgetForm', …)`).
 *
 * Identical to `buildCpWidgetForm` (`hideFields` + `fieldsConfigured`: only the listed columns, in
 * this order, with these German headers) — kept as a separate builder so the shared helper of the
 * module does not change underneath its other hosts.
 *
 * @param columns the visible columns, in the order they should appear
 */
export function buildCpRunWidgetForm(columns: CpRunWidgetColumn[]): WidgetForm {

    const layout = new WidgetConfigFormLayout();
    layout.hideFields = true;
    layout.fieldsConfigured = true;

    columns.forEach((column, index) => {
        const field = WidgetConfigFormField.create(column.field, '', '');
        field.uiLabel = column.label;
        field.uiPriority = index;
        if (column.outputControl) {
            field.uiOutputField = column.outputControl;
        }
        layout.addField(field);
    });

    const widgetForm = new WidgetForm();
    widgetForm.setDefaultLayout(layout);

    return widgetForm;
}
